import type { Money } from './common';
import type { HostStatus } from './home';
export const CHAT_INTENTS = [
  'BEAUTY',
  'TRIP',
  'COST',
  'CLINIC',
  'QUESTION',
  'UNSURE',
] as const;
export type ChatIntent = (typeof CHAT_INTENTS)[number];
export interface ChatRoom {
  id: string;
  type: 'HOST' | 'CLINIC' | 'AI';
  name: string;
  subtitle: string;
  status: HostStatus;
  preview: string;
  unreadCount: number;
  updatedAt: string;
  replyMinutes: number;
  diagnosisId?: string;
  proposalSetId?: string;
}
export type FormCode = 'TRAVEL_DATES' | 'SKIN_CONCERNS' | 'BUDGET_RANGE';
export type FormValue =
  { startDate: string; endDate: string } | string[] | string;
export type MessageCard =
  | {
      type: 'PLANNER_PREVIEW';
      title: string;
      description: string;
      plannerPreviewId: string;
      days: number;
      price: Money;
    }
  | {
      type: 'FORM_REQUEST';
      code: FormCode;
      title: string;
      options: { code: string; label: string }[];
      response?: FormValue;
    }
  | {
      type: 'PLAN_PROPOSAL';
      title: string;
      proposalSetId: string;
      optionCount: number;
    }
  | {
      type: 'VOUCHER_OFFER';
      title: string;
      description: string;
      price: Money;
      proposalSetId: string;
    }
  | { type: 'PAYMENT_LINK'; title: string; orderId: string }
  | {
      type: 'PRICE_GUARANTEE' | 'POLICY_SUMMARY';
      title: string;
      description: string;
    }
  | { type: 'BOOKING'; title: string; bookingId: string; status: string }
  | { type: 'CHECKLIST'; title: string; items: string[] }
  | { type: 'AFTERCARE_CHECKIN'; title: string; planId: string };
export interface ChatAttachment {
  id: string;
  name: string;
  mime: string;
  sizeBytes: number;
}
export interface ChatMessage {
  id: string;
  clientMessageId?: string;
  senderType: 'MEMBER' | 'HOST' | 'CLINIC' | 'SYSTEM';
  senderName: string;
  text?: string;
  card?: MessageCard;
  attachment?: ChatAttachment;
  createdAt: string;
  read: boolean;
  quotedOption?: string;
  quotedOptionId?: string;
}
export interface MessagePage {
  count: number;
  rows: ChatMessage[];
  nextCursor?: string;
  isLast: boolean;
}
export interface SendMessageBody {
  clientMessageId: string;
  text?: string;
  attachmentId?: string;
  quotedOptionId?: string;
}
export interface PlanOption {
  id: string;
  type: 'BEST_MATCH' | 'BEST_VALUE' | 'PREMIUM_CHOICE';
  title: string;
  clinic: string;
  treatments: string[];
  whyRecommended: string;
  listPrice: Money;
  memberPrice: Money;
  recovery: string;
  recommendedDate: string;
  guarantees: string[];
}
export interface ProposalSet {
  id: string;
  roomId: string;
  intro: string;
  status: 'SENT' | 'ACCEPTED' | 'EXPIRED';
  expiresAt: string;
  schedule: { code: string; date: string; label: string }[];
  notice: string;
  options: PlanOption[];
}
